import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Reusable file upload component with drag and drop functionality
 * 
 * @param {Object} props
 * @param {Function} props.onFileSelect - Callback function when file is selected (legacy)
 * @param {Function} props.onFileSelected - Callback function when file is selected
 * @param {string} props.acceptedFileTypes - File types to accept (e.g., '.xlsx') (legacy)
 * @param {string} props.accept - MIME type for file input (e.g., 'image/*')
 * @param {Array} props.acceptedFormats - Array of accepted file extensions (e.g., ['.jpg', '.png'])
 * @param {number} props.maxSize - Maximum file size in bytes
 * @param {number} props.maxSizeMB - Maximum file size in MB
 * @param {string} props.label - Label for the uploader
 * @param {boolean} props.disabled - Whether the uploader is disabled
 * @param {string} props.defaultPreview - URL of default image preview
 * @param {boolean} props.allowRemove - Whether to allow removing the file
 */
const FileUploader = ({ 
  onFileSelect,
  onFileSelected,
  acceptedFileTypes = '.xlsx', 
  accept,
  acceptedFormats,
  maxSize,
  maxSizeMB = 5,
  label = null,
  disabled = false,
  defaultPreview = null,
  allowRemove = true
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(defaultPreview);
  const fileInputRef = useRef(null);
  
  // Use the provided callback or fallback for backward compatibility
  const handleFileSelected = onFileSelected || onFileSelect;
  
  // Use accept prop if provided, otherwise use acceptedFileTypes for backward compatibility
  const acceptValue = accept || acceptedFileTypes;
  
  // Determine if this is an image uploader
  const isImageUploader = accept === 'image/*' || (acceptedFormats && acceptedFormats.some(format => 
    ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp'].includes(format.toLowerCase())
  ));
  
  // Convert MB to bytes if maxSize is not directly provided
  const maxSizeBytes = maxSize || (maxSizeMB * 1024 * 1024);
  
  // Reset preview when defaultPreview changes
  useEffect(() => {
    setPreview(defaultPreview);
  }, [defaultPreview]);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const validateFile = (file) => {
    // Reset error
    setError('');
    
    // Check file type
    if (acceptedFormats && acceptedFormats.length > 0) {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const isValidExtension = acceptedFormats.some(format => 
        fileExtension === format.toLowerCase() || 
        format.toLowerCase() === '.' + file.type.split('/').pop()
      );
      
      if (!isValidExtension) {
        setError(t('fileUploader.invalidType', { acceptedTypes: acceptedFormats.join(', ') }));
        return false;
      }
    } else {
      // Use old validation if acceptedFormats not provided
      const fileType = file.name.toLowerCase().split('.').pop();
      const acceptedTypes = acceptedFileTypes
        .split(',')
        .map(type => type.trim().replace('.', '').toLowerCase());
        
      if (!acceptedTypes.includes(fileType)) {
        setError(t('fileUploader.invalidType', { acceptedTypes: acceptedFileTypes }));
        return false;
      }
    }
    
    // Check file size
    if (file.size > maxSizeBytes) {
      setError(t('fileUploader.fileTooLarge', { maxSize: maxSizeMB }));
      return false;
    }
    
    return true;
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (disabled) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setFileName(file.name);
        
        // Create preview URL for images
        if (isImageUploader) {
          const previewUrl = URL.createObjectURL(file);
          setPreview(previewUrl);
        }
        
        handleFileSelected(file);
      }
    }
  };
  
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setFileName(file.name);
        
        // Create preview URL for images
        if (isImageUploader) {
          const previewUrl = URL.createObjectURL(file);
          setPreview(previewUrl);
        }
        
        handleFileSelected(file);
      }
    }
  };
  
  const handleButtonClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleRemoveFile = (e) => {
    e.stopPropagation();
    if (!disabled && allowRemove) {
      setFileName('');
      setPreview(null);
      handleFileSelected(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  return (
    <div className="w-full">
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      
      <div 
        className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400 hover:bg-gray-50'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        <input
          type="file"
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          accept={acceptValue}
          disabled={disabled}
        />
        
        <div className="space-y-2">
          {(fileName || preview) ? (
            <>
              {isImageUploader && preview && (
                <div className="mb-3 flex justify-center">
                  <img 
                    src={preview} 
                    alt={fileName || "Uploaded image"} 
                    className="max-h-32 max-w-full object-contain"
                  />
                </div>
              )}
              
              {fileName && (
                <div className="text-sm font-medium text-gray-900">{fileName}</div>
              )}
              
              <p className="text-xs text-gray-500">
                {fileName ? t('fileUploader.fileSelected') : t('fileUploader.currentFile')}
              </p>
              
              <div className="mt-2 flex flex-col items-center">
                <svg className="w-5 h-5 text-blue-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <p className="text-xs text-blue-500">
                  {t('fileUploader.clickToReplace')}
                </p>
              </div>
              
              {allowRemove && (
                <button 
                  className={`mt-2 inline-flex items-center px-3 py-1 border border-transparent text-xs leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-500 focus:outline-none ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  onClick={handleRemoveFile}
                  disabled={disabled}
                  type="button"
                >
                  {t('fileUploader.clear')}
                </button>
              )}
            </>
          ) : (
            <>
              <svg 
                className="mx-auto h-12 w-12 text-gray-400" 
                stroke="currentColor" 
                fill="none" 
                viewBox="0 0 48 48"
              >
                <path 
                  d="M24 8l8 8h-6v11h-4V16h-6l8-8z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  fill="none"
                />
                <path 
                  d="M12 28v8a2 2 0 002 2h20a2 2 0 002-2v-8" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  fill="none"
                />
              </svg>
              <p className="text-sm text-gray-600">
                {t('fileUploader.dragDrop')} {t('fileUploader.or')} <span className="text-blue-500">{t('fileUploader.browse')}</span>
              </p>
              <p className="text-xs text-gray-500">
                {acceptedFormats 
                  ? `${t('fileUploader.acceptedTypes')}: ${acceptedFormats.join(', ')}`
                  : `${t('fileUploader.acceptedTypes')}: ${acceptValue}`
                }
              </p>
              <p className="text-xs text-gray-500">
                {t('fileUploader.maxSize')}: {maxSizeMB} MB
              </p>
            </>
          )}
        </div>
      </div>
      
      {error && (
        <div className="mt-2 text-sm text-red-600">{error}</div>
      )}
    </div>
  );
};

export default FileUploader; 